import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ISchoolYear } from 'src/shared/interfaces';
import { AuthState } from 'src/shared/store/state/auth.state';
import { SchoolYearState } from './school-year.state';

import { IClass } from '../../interfaces/groups/class.interface';
import { IQuestionChoices } from '../../interfaces/radio-checkbox-config.interface';
import { IUser } from '../../interfaces/users/user.interface';
import { ClassesService } from '../../services/classes.service';
import {
    CreateNewClass,
    DeleteCachedClasses,
    DeleteClass,
    DeleteClassToEdit,
    DisableClass,
    EnableClass,
    GetAllClasses,
    GetClassById,
    PatchClass,
} from '../actions/classes.action';

export interface ClassesStateModel {
    classes: IClass[];
    classToEdit: IClass;
}

@State<ClassesStateModel>({
    name: 'classes',
    defaults: {
        classes: [],
        classToEdit: null,
    },
})
@Injectable()
export class ClassesState {
    constructor(private classesService: ClassesService, private store: Store) {}

    @Selector()
    public static getAllClasses(state: ClassesStateModel): IClass[] {
        return state.classes;
    }

    @Selector([SchoolYearState.getCurrentSchoolYear])
    public static getCurrentYearClasses(state: ClassesStateModel, currentSchoolYear: ISchoolYear): IClass[] {
        return state.classes.filter((c: IClass) => c.schoolYear?.id === currentSchoolYear?.id);
    }

    @Selector([SchoolYearState.getAllSchoolYear])
    public static schoolHasClassesInTheMostRecentYear(state: ClassesStateModel, schoolYears: ISchoolYear[]): boolean {
        // = classes par encore transférées dans année la + recente,
        // car classe dans le passé (state.classes) mais aucune avec schoolYear égale au dernier schoolYear
        return state.classes.length && state.classes.some((c: IClass) => c.schoolYear.id === schoolYears[schoolYears.length - 1].id);
    }

    @Selector([SchoolYearState.getAllSchoolYear])
    public static schoolHasClassesInPreviousYear(state: ClassesStateModel, schoolYears: ISchoolYear[]): boolean {
        if (schoolYears.length <= 1 || !state.classes.length) {
            return false;
        } else {
            return state.classes.length && state.classes.some((c: IClass) => c.schoolYear.id === schoolYears[schoolYears.length - 2].id);
        }
    }

    @Selector()
    public static classToEdit(state: ClassesStateModel): IClass {
        return state.classToEdit;
    }

    @Selector([SchoolYearState.getCurrentSchoolYear])
    public static getCurrentYearClassesChoices(state: ClassesStateModel, currentSchoolYear: ISchoolYear): IQuestionChoices[] {
        return this.getCurrentYearClasses(state, currentSchoolYear).map((schoolClass: IClass) => ({
            value: schoolClass.id,
            label: schoolClass.name,
        }));
    }

    // READ
    @Action(GetAllClasses)
    public getAllClasses(ctx: StateContext<ClassesStateModel>, action: GetAllClasses): Observable<IClass[]> | null {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        if ((connectedUser.isAdmin || ctx.getState().classes.length) && !action.forceCall) {
            return null;
        }
        return this.classesService.getAllClasses(selectedUser).pipe(
            tap((result: IClass[]) => {
                if (result) {
                    ctx.patchState({
                        classes: result,
                    });
                }
            })
        );
    }

    @Action(GetClassById)
    public setClassToEdit(ctx: StateContext<ClassesStateModel>, action: GetClassById): Observable<IClass> | void {
        const state: ClassesStateModel = ctx.getState();
        const classFound: IClass = state.classes.find((classZ: IClass) => classZ.id === action.id);
        if (!state.classes.length && !classFound) {
            return this.classesService.getClassById(action.id).pipe(
                tap((result: IClass) => {
                    if (result) {
                        ctx.patchState({
                            classToEdit: result,
                        });
                    }
                })
            );
        } else {
            ctx.patchState({
                classToEdit: classFound,
            });
        }
    }

    // CREATE
    @Action(CreateNewClass)
    public createNewClass(ctx: StateContext<ClassesStateModel>, action: CreateNewClass): Observable<IClass> {
        return this.classesService.createClass(action.newClass).pipe(
            tap((result: IClass) => {
                ctx.setState(
                    patch({
                        classToEdit: null,
                        classes: append([result]),
                    })
                );
            })
        );
    }

    // UPDATE
    @Action(PatchClass)
    public updateClass(ctx: StateContext<ClassesStateModel>, action: PatchClass): Observable<IClass> {
        const schoolIdToUpdate: number = action.schoolClass.school_class.id;
        return this.classesService.patchClass(action.schoolClass, schoolIdToUpdate).pipe(
            tap((result: IClass) => {
                ctx.setState(
                    patch({
                        classes: updateItem<IClass>((entity: IClass) => entity.id === schoolIdToUpdate, result),
                    })
                );
            })
        );
    }

    // DELETE
    @Action(DeleteClass)
    public deleteSchool(ctx: StateContext<ClassesStateModel>, action: DeleteClass): Observable<void> {
        return this.classesService.deleteClass(action.classId).pipe(
            tap(() => {
                ctx.patchState({
                    classes: ctx.getState().classes.filter((schoolClass: IClass) => schoolClass.id !== action.classId),
                });
            })
        );
    }

    // DELETE -> ENABLE & DISABLE
    @Action(EnableClass)
    public enableClass(ctx: StateContext<ClassesStateModel>, action: EnableClass): Observable<IClass> {
        return this.classesService.enableClass(action.classId).pipe(
            tap((result: IClass) => {
                ctx.setState(
                    patch({
                        classes: updateItem<IClass>((school: IClass) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    @Action(DisableClass)
    public disableClass(ctx: StateContext<ClassesStateModel>, action: DisableClass): Observable<IClass> {
        return this.classesService.disableClass(action.classId).pipe(
            tap((result: IClass) => {
                ctx.setState(
                    patch({
                        classes: updateItem<IClass>((school: IClass) => school.id === result.id, result),
                    })
                );
            })
        );
    }

    // UTILS
    @Action(DeleteClassToEdit)
    public deleteClassToEdit(ctx: StateContext<ClassesStateModel>): void {
        ctx.patchState({
            classToEdit: null,
        });
    }

    @Action(DeleteCachedClasses)
    public deleteCachedGroups(ctx: StateContext<ClassesStateModel>): void {
        ctx.patchState({
            classes: [],
        });
    }
}
